import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Clock, ChevronDown } from "lucide-react";
import { IMG } from "../data/images";
import { SITE, scrollToId } from "../data/site";
import { MaskedLine, EASE } from "./Reveal";
import { OrderButton } from "./OrderDialog";
import { useLang } from "../i18n";

export const Hero = () => {
    const { t } = useLang();
    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 900], [0, 220]);
    const fade = useTransform(scrollY, [0, 500], [1, 0]);

    return (
        <section id="home" data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden">
            <motion.div style={{ y }} className="absolute inset-0 -bottom-56">
                <img
                    src={IMG.hero}
                    alt="Authentic Indian curries and naan at Royal Tadka Kraków"
                    className="w-full h-full object-cover animate-kenburns"
                    fetchpriority="high"
                    decoding="async"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/35" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-transparent to-transparent" />

            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-28 pt-40 w-full"
            >
                <motion.p
                    initial={{ opacity: 0, letterSpacing: "0.6em" }}
                    animate={{ opacity: 1, letterSpacing: "0.35em" }}
                    transition={{ duration: 1.2, delay: 0.4, ease: EASE }}
                    className="text-[11px] sm:text-xs uppercase font-semibold text-saffron-glow/90 mb-6"
                >
                    {t("hero.overline")}
                </motion.p>

                <h1 className="font-serif font-semibold tracking-tight leading-[1.02] text-5xl sm:text-7xl lg:text-8xl text-cream text-balance">
                    <MaskedLine delay={0.55}>THE ROYAL</MaskedLine>
                    <MaskedLine delay={0.7}>TASTE OF</MaskedLine>
                    <MaskedLine delay={0.85} className="pr-3" innerClass="italic gold-text">
                        INDIA
                    </MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.15, ease: EASE }}
                    className="mt-6 max-w-xl text-base sm:text-lg text-stone-300 font-light leading-relaxed"
                >
                        {t("hero.sub")}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.3, ease: EASE }}
                    className="mt-9 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4"
                >
                    <OrderButton testId="hero-order-online-button" className="w-full sm:w-auto px-9 py-4 text-sm">
                        {t("nav.order")}
                    </OrderButton>
                    <motion.button
                        data-testid="hero-explore-menu-button"
                        onClick={() => scrollToId("favourites")}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        className="w-full sm:w-auto px-9 py-4 text-sm rounded-full gold-border text-cream font-semibold tracking-wide hover:border-saffron/60 hover:text-saffron-glow transition-[border-color,color] duration-300"
                    >
                                {t("hero.explore")}
                    </motion.button>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.2, delay: 1.6 }}
                    className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs sm:text-sm text-stone-400"
                >
                    <span data-testid="hero-location-chip" className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-saffron-bright" strokeWidth={1.5} />
                        {t("hero.loc")}
                    </span>
                    <span className="hidden sm:inline text-brass/50">✦</span>
                    <span data-testid="hero-hours-chip" className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-saffron-bright" strokeWidth={1.5} />
                        {t("hero.hours")}
                    </span>
                </motion.div>
            </motion.div>

            <motion.button
                data-testid="hero-scroll-indicator"
                onClick={() => scrollToId("experience")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-stone-500 hover:text-saffron-glow transition-colors duration-300"
                aria-label="Scroll down"
            >
                <motion.span
                    animate={{ y: [0, 8, 0] }}
                    transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
                    className="block"
                >
                    <ChevronDown className="w-6 h-6" strokeWidth={1.25} />
                </motion.span>
            </motion.button>
        </section>
    );
};
